import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CertificationsSection = ({ certifications, onAdd, onDelete }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newCert, setNewCert] = useState({
    name: '',
    issuer: '',
    issueDate: '',
    credentialId: '',
    credentialUrl: ''
  });

  const handleAddCertification = () => {
    if (newCert?.name && newCert?.issuer) {
      onAdd(newCert);
      setNewCert({
        name: '',
        issuer: '',
        issueDate: '',
        credentialId: '',
        credentialUrl: ''
      });
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-card-foreground">
          Certifications & Achievements
        </h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setIsAdding(!isAdding)}
        >
          Add Certification
        </Button>
      </div>
      {isAdding && (
        <div className="bg-muted/50 rounded-lg p-4 md:p-6 border border-border space-y-4">
          <Input
            label="Certification Name"
            type="text"
            placeholder="AWS Certified Developer, Google Analytics, etc."
            value={newCert?.name}
            onChange={(e) => setNewCert({ ...newCert, name: e?.target?.value })}
            required
          />

          <Input
            label="Issuing Organization"
            type="text"
            placeholder="Amazon, Google, Microsoft, etc."
            value={newCert?.issuer}
            onChange={(e) => setNewCert({ ...newCert, issuer: e?.target?.value })}
            required
          />

          <Input
            label="Issue Date"
            type="date"
            value={newCert?.issueDate}
            onChange={(e) => setNewCert({ ...newCert, issueDate: e?.target?.value })}
          />

          <Input
            label="Credential ID"
            type="text"
            placeholder="Certificate ID or number"
            value={newCert?.credentialId}
            onChange={(e) => setNewCert({ ...newCert, credentialId: e?.target?.value })}
          />

          <Input
            label="Credential URL"
            type="url"
            placeholder="https://verify.certificate.com/..."
            value={newCert?.credentialUrl}
            onChange={(e) => setNewCert({ ...newCert, credentialUrl: e?.target?.value })}
          />

          <div className="flex gap-3">
            <Button variant="default" onClick={handleAddCertification}>
              Save Certification
            </Button>
            <Button variant="outline" onClick={() => setIsAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {certifications?.map((cert, index) => (
          <div
            key={index}
            className="bg-card rounded-lg border border-border p-4 md:p-6 transition-smooth hover:shadow-warm-md"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Award" size={24} color="var(--color-success)" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-semibold text-card-foreground mb-1 line-clamp-2">
                  {cert?.name}
                </h4>
                <p className="text-sm text-muted-foreground mb-2">{cert?.issuer}</p>
                {cert?.issueDate && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Issued: {new Date(cert.issueDate)?.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                  </p>
                )}
                {cert?.credentialId && (
                  <p className="text-xs text-muted-foreground mb-2">
                    ID: {cert?.credentialId}
                  </p>
                )}
                <div className="flex gap-2 mt-3">
                  {cert?.credentialUrl && (
                    <a
                      href={cert?.credentialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <Icon name="ExternalLink" size={12} />
                      Verify
                    </a>
                  )}
                  <button
                    onClick={() => onDelete(index)}
                    className="text-xs text-error hover:underline flex items-center gap-1"
                  >
                    <Icon name="Trash2" size={12} />
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {certifications?.length === 0 && !isAdding && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed border-border">
          <Icon name="Award" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No certifications added yet</p>
          <Button variant="outline" onClick={() => setIsAdding(true)}>
            Add Your First Certification
          </Button>
        </div>
      )}
    </div>
  );
};

export default CertificationsSection;